import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Building2, ShieldCheck, Plus, Trash2, Save, Loader2, Inbox, BadgeEuro, UserPlus, Mail, Phone, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { displayName } from "../lib/anagConfig";
import NuovoProfessionistaDialog from "./NuovoProfessionistaDialog";
import CreatableTipoUfficio from "./TipoUfficioField";
import MultiEmail from "./MultiEmail";
import HistoryInput from "./HistoryInput";
import ResizableNote from "./ResizableNote";
import AddressAutocomplete from "./AddressAutocomplete";
import AutocompleteInput from "./AutocompleteInput";
import CampiPraticaEditor from "./CampiPraticaEditor";
import { DEFAULT_ISPETTORATO_FIELDS } from "../lib/anagConfig";

const MULTI = { telefono: { type: "tel", star: false, label: "telefono", altLabel: "altro telefono" }, email: { label: "email" }, pec: { label: "PEC" } };
const splitCivico = (s) => {
  const m = String(s || "").trim().match(/^(.*?)[,\s]+(\d+[A-Za-z]?(?:\/\w+)?)$/);
  return m ? { via: m[1], civico: m[2] } : { via: String(s || "").trim(), civico: "" };
};

function LiquidatoriPerIspettorato({ ispettorati, compName }) {
  const [liq, setLiq] = useState([]);
  const norm = (s) => (s || "").trim().toLowerCase();
  useEffect(() => { api.get("/anagrafiche/liquidatori").then(({ data }) => setLiq(data || [])).catch(() => setLiq([])); }, []);
  const names = (ispettorati || []).map((i) => (i?.denominazione || "").trim()).filter(Boolean);
  if (!names.length) return null;
  // Considera solo i liquidatori associati alla compagnia corrente (evita di mostrare liquidatori di altre compagnie con ispettorati omonimi).
  const compLiq = liq.filter((l) => (l.compagnie || []).some((c) => norm(c) === norm(compName)));
  return (
    <div className="mt-4" data-testid="isp-liquidatori-assoc">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-mono mb-2">Lista ispettorati</div>
      <div className="space-y-2">
        {names.map((n) => {
          const ls = compLiq.filter((l) => (l.ispettorati || []).includes(n));
          return (
            <div key={n} className="rounded-md border border-slate-700 bg-slate-900/40 p-2.5 text-sm">
              <div className="text-slate-200 font-medium">{n}</div>
              {ls.length ? (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {ls.map((l) => <span key={l.id} className="text-xs rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30 px-2 py-0.5">{displayName(l)}</span>)}
                </div>
              ) : <div className="text-xs text-slate-500 mt-0.5">Nessun liquidatore associato</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Tab "Liquidatori": elenca i liquidatori associati alla compagnia corrente e consente di
// aggiungerne uno nuovo con la compagnia già precompilata.
function LiquidatoriTab({ compName }) {
  const [liq, setLiq] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [liqOpen, setLiqOpen] = useState({}); // di default ogni scheda liquidatore è chiusa
  const norm = (s) => (s || "").trim().toLowerCase();

  const load = () => {
    setLoading(true);
    api.get("/anagrafiche/liquidatori")
      .then(({ data }) => setLiq((data || []).filter((l) => (l.compagnie || []).some((c) => norm(c) === norm(compName)))))
      .catch(() => setLiq([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [compName]);

  const unlink = async (l, name) => {
    if (!window.confirm(`Rimuovere ${name} da questa compagnia?`)) return;
    const newComp = (l.compagnie || []).filter((c) => norm(c) !== norm(compName));
    try {
      await api.patch(`/anagrafiche/liquidatori/${l.id}`, { compagnie: newComp });
      toast.success("Liquidatore rimosso dalla compagnia");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mb-2" data-testid="liq-section">
      <div style={{ backgroundColor: "#7c2d12" }} className="flex items-center gap-2 px-4 py-2 rounded-t-lg text-white text-xs font-bold uppercase tracking-widest">
        <BadgeEuro className="h-4 w-4" /> Liquidatori associati
      </div>
      <div className="border border-t-0 border-slate-800 rounded-b-lg p-4 bg-slate-900/20 min-w-0 space-y-3">
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
        ) : liq.length === 0 ? (
          <p className="text-center text-slate-500 text-xs py-4">Nessun liquidatore associato a questa compagnia.</p>
        ) : (
          <div className="space-y-2">
            {liq.map((l) => {
              const open = !!liqOpen[l.id];
              const name = [l.cognome, l.nome].filter(Boolean).join(" ") || displayName(l);
              return (
                <div key={l.id} data-testid={`liq-row-${l.id}`} className="rounded-lg border border-slate-800 bg-slate-900/40 overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2.5">
                    <button type="button" onClick={() => setLiqOpen((s) => ({ ...s, [l.id]: !s[l.id] }))} data-testid={`liq-toggle-${l.id}`} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                      {open ? <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />}
                      <span className="text-sm font-medium text-slate-100 truncate">{name}</span>
                    </button>
                    <button type="button" onClick={() => unlink(l, name)} data-testid={`liq-del-${l.id}`} title="Rimuovi da questa compagnia"
                      className="p-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {open && (
                    <div className="px-4 pb-3 -mt-0.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-amber-400/70" /> {l.telefono || l.cellulare || "—"}</span>
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3 text-amber-400/70" /> {l.email || "—"}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="pt-1">
          <Button type="button" size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="liq-add"
            className="border-slate-700 text-slate-200 hover:bg-slate-800 h-9">
            <UserPlus className="h-4 w-4 mr-1.5" /> Aggiungi liquidatore
          </Button>
        </div>
      </div>
      {addOpen && (
        <NuovoProfessionistaDialog
          open={addOpen}
          atype="liquidatori"
          preset={{ compagnie: compName ? [compName] : [] }}
          onSaved={() => { setAddOpen(false); load(); toast.success("Liquidatore aggiunto"); }}
          onCancel={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

export const ISP_COLS = [
  { key: "denominazione", label: "Nome", full: true },
  { key: "tipo_ufficio", label: "Tipo ufficio", type: "tipoufficio", full: true },
  { key: "indirizzo", label: "Indirizzo", full: true },
  { key: "numero_civico", label: "N° Civico" },
  { key: "cap", label: "CAP" },
  { key: "comune", label: "Comune" },
  { key: "provincia", label: "Provincia" },
  { key: "telefono", label: "Telefono" },
  { key: "fax", label: "Fax" },
  { key: "email", label: "Email" },
  { key: "pec", label: "PEC" },
  { key: "referente", label: "Referente" },
  { key: "note", label: "Note", full: true },
];

// Normalizza una voce (ispettorato/agenzia/reclami): campi testo, indirizzi aggiuntivi e campi visibili in pratica.
export const buildRow = (r, cols) => {
  const o = { id: r.id || (window.crypto?.randomUUID ? window.crypto.randomUUID() : `r${Date.now()}${Math.random().toString(36).slice(2, 8)}`) };
  cols.forEach((c) => { o[c.key] = (r[c.key] || "").trim(); });
  Object.keys(MULTI).forEach((k) => {
    const ex = (Array.isArray(r[`${k}_extra`]) ? r[`${k}_extra`] : []).map((x) => (x || "").trim()).filter(Boolean);
    if (cols.some((c) => c.key === k) && ex.length) {
      if (!o[k]) o[k] = ex.shift();
      if (ex.length) o[`${k}_extra`] = ex;
    }
  });
  if (Array.isArray(r.campi_pratica)) o.campi_pratica = r.campi_pratica;
  if (o.provincia) o.provincia = o.provincia.toUpperCase();
  return o;
};

// Scheda ispettorato unica: usata in Compagnie Assicurative e dalla matita in pratica.
export function IspettoratoForm({ row, setRow, testId, pool, cols = ISP_COLS, noteKey = "isp" }) {
  const hasCivico = cols.some((c) => c.key === "numero_civico");
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid={`${testId}-form`}>
      {cols.map((c) => (
        <SectionField key={c.key} col={c} row={row} setRow={setRow} testId={`${testId}-${c.key}`} pool={pool || {}} hasCivico={hasCivico} noteKey={noteKey} />
      ))}
      <div className="sm:col-span-2">
        <CampiPraticaEditor value={row.campi_pratica} onChange={(v) => setRow({ campi_pratica: v })} defaultKeys={DEFAULT_ISPETTORATO_FIELDS} testId={`${testId}-campi`} />
      </div>
    </div>
  );
}

export const ispPool = (comps) => {
  const out = {};
  (comps || []).forEach((c) => ["ispettorati", "agenzie", "uffici_reclami"].forEach((s) => (c[s] || []).forEach((r) => {
    Object.entries(r || {}).forEach(([k, v]) => { if (typeof v === "string" && v.trim()) (out[k] = out[k] || []).push(v.trim()); });
  })));
  return out;
};

export const AGE_COLS = [
  { key: "codice_agenzia", label: "Codice Agenzia" },
  { key: "denominazione", label: "Nome" },
  { key: "tipo_ufficio", label: "Tipo ufficio", type: "tipoufficio", full: true },
  { key: "indirizzo", label: "Indirizzo", full: true },
  { key: "cap", label: "CAP" },
  { key: "comune", label: "Comune" },
  { key: "provincia", label: "Provincia" },
  { key: "telefono", label: "Telefono" },
  { key: "fax", label: "Fax" },
  { key: "email", label: "Email" },
  { key: "pec", label: "PEC" },
  { key: "responsabile", label: "Referente" },
  { key: "note", label: "Note", full: true },
];

const RECLAMI_COLS = [
  { key: "denominazione", label: "Intestazione", full: true },
  { key: "indirizzo", label: "Indirizzo", full: true },
  { key: "numero_civico", label: "N° Civ." },
  { key: "cap", label: "CAP" },
  { key: "comune", label: "Comune" },
  { key: "provincia", label: "Provincia" },
  { key: "telefono", label: "Telefono" },
  { key: "fax", label: "Fax" },
  { key: "email", label: "Email" },
  { key: "pec", label: "PEC" },
  { key: "referente", label: "Referente" },
  { key: "note", label: "Note", full: true },
];

function SectionField({ col, row, setRow, testId, pool, hasCivico, noteKey }) {
  const value = row[col.key];
  const onChange = (v) => setRow({ [col.key]: v });
  const m = MULTI[col.key];
  const inputCls = "h-9 w-full px-3 rounded-md bg-slate-900/60 border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-cyan-500/50";
  return (
    <div className={col.full ? "sm:col-span-2" : ""}>
      <Label className="text-slate-300 text-sm">{col.label}</Label>
      {col.type === "tipoufficio" ? (
        <CreatableTipoUfficio value={value} onChange={onChange} testId={testId} />
      ) : m ? (
        <MultiEmail value={value} extra={row[`${col.key}_extra`]} testId={testId} type={m.type || "email"} star={m.star !== false} label={m.label} altLabel={m.altLabel}
          onChange={(main, ex) => setRow({ [col.key]: main, [`${col.key}_extra`]: ex })} />
      ) : col.key === "note" ? (
        <ResizableNote value={value} onChange={onChange} storageKey={noteKey} testId={testId} />
      ) : col.key === "indirizzo" ? (
        <div className="mt-1.5">
          <AddressAutocomplete value={value || ""} onChange={onChange} city={row.comune} prov={row.provincia} testId={testId} inputClassName={inputCls}
            onPick={(p) => {
              const s = hasCivico ? splitCivico(p.indirizzo) : { via: p.indirizzo, civico: "" };
              setRow({ indirizzo: s.via, ...(hasCivico && s.civico ? { numero_civico: s.civico } : {}), comune: p.comune || row.comune || "", provincia: (p.provincia || row.provincia || "").toUpperCase(), cap: p.cap || row.cap || "" });
            }} />
        </div>
      ) : col.key === "comune" ? (
        <div className="mt-1.5"><AutocompleteInput source="comuni" value={value || ""} onChange={onChange} testId={testId} className="h-9 bg-slate-900/60 border-slate-700"
          onSelectItem={(it) => setRow({ comune: it.nome, provincia: it.provincia || row.provincia || "", cap: it.cap || row.cap || "" })} /></div>
      ) : col.type === "select" ? (
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger data-testid={testId} className="mt-1.5 h-9 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
            {col.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <HistoryInput value={value} onChange={onChange} options={pool[col.key] || []} testId={testId} />
      )}
    </div>
  );
}

function SectionTable({ icon: Icon, title, color, columns, rows, setRows, onSave, saving, testId, newRow, pool }) {
  const [openRows, setOpenRows] = useState({}); // di default ogni scheda è chiusa
  const setCells = (i, patch) => setRows((arr) => arr.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const hasCivico = columns.some((c) => c.key === "numero_civico");
  const addRow = () => { setRows((arr) => { setOpenRows((o) => ({ ...o, [arr.length]: true })); return [...arr, newRow ? newRow() : {}]; }); };
  const delRow = (i) => setRows((arr) => arr.filter((_, idx) => idx !== i));
  const toggleRow = (i) => setOpenRows((o) => ({ ...o, [i]: !o[i] }));
  const word = title.split(" ")[0];

  return (
    <div className="mb-2" data-testid={`${testId}-section`}>
      <div style={{ backgroundColor: color }} className="flex items-center gap-2 px-4 py-2 rounded-t-lg text-white text-xs font-bold uppercase tracking-widest">
        <Icon className="h-4 w-4" /> {title}
      </div>
      <div className="border border-t-0 border-slate-800 rounded-b-lg p-4 bg-slate-900/20 min-w-0 space-y-3">
        {rows.length === 0 ? (
          <p className="text-center text-slate-500 text-xs py-4">Nessuna voce. Usa "Aggiungi".</p>
        ) : rows.map((r, i) => {
          const open = !!openRows[i];
          return (
            <div key={i} data-testid={`${testId}-row-${i}`} className="rounded-lg border border-slate-800 bg-slate-900/40 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2.5">
                <button type="button" onClick={() => toggleRow(i)} data-testid={`${testId}-toggle-${i}`} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                  {open ? <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />}
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-amber-300 shrink-0">{word} #{i + 1}</span>
                  {!open && (r.denominazione || "").trim() && <span className="text-xs text-slate-400 truncate">— {r.denominazione}</span>}
                </button>
                <button type="button" onClick={() => delRow(i)} data-testid={`${testId}-del-${i}`}
                  className="p-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {open && (testId === "isp" || testId === "age" ? (
                <div className="px-4 pb-4">
                  <IspettoratoForm row={r} setRow={(patch) => setCells(i, patch)} testId={`${testId}-${i}`} pool={pool} cols={columns} noteKey={testId} />
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4 px-4 pb-4">
                  {columns.map((c) => (
                    <SectionField key={c.key} col={c} row={r} setRow={(patch) => setCells(i, patch)} testId={`${testId}-${i}-${c.key}`}
                      pool={pool || {}} hasCivico={hasCivico} noteKey={testId} />
                  ))}
                </div>
              ))}
            </div>
          );
        })}
        <div className="flex items-center justify-between pt-1">
          <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid={`${testId}-add`}
            className="border-slate-700 text-slate-200 hover:bg-slate-800 h-9">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi
          </Button>
          <Button type="button" size="sm" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}
            className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold h-9">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Salva {word}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Lista riepilogativa delle voci inserite, mostrata sotto il form (Agenzie / Ufficio Reclami).
function EntriesSummary({ testId, rows, cols }) {
  const items = rows.filter((r) => cols.some((c) => (r[c.key] || "").trim()));
  if (!items.length) return null;
  return (
    <div className="mt-4" data-testid={`${testId}-summary`}>
      <div className="text-xs uppercase tracking-wider text-slate-400 font-mono mb-2">Riepilogo voci inserite</div>
      <div className="space-y-1.5">
        {items.map((r, i) => (
          <div key={i} data-testid={`${testId}-summary-row-${i}`} className="rounded-md border border-slate-700 bg-slate-900/40 px-3 py-2 text-sm">
            <div className="text-slate-200 font-medium">{(r.denominazione || "").trim() || `Voce #${i + 1}`}</div>
            <div className="text-xs text-slate-500 mt-0.5">{[[r.indirizzo, r.numero_civico].map((x) => (x || "").trim()).filter(Boolean).join(" "), r.comune, r.provincia].map((x) => (x || "").trim()).filter(Boolean).join(" · ") || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Editor riusabile delle sottosezioni di una compagnia: Ispettorati e Agenzie.
// Ogni sezione ha il proprio salvataggio (PATCH parziale) e i dati sono legati alla compagnia (rec.id).
export default function CompagniaSezioni({ rec, onSaved, onChange }) {
  const [ispettorati, setIspettorati] = useState([]);
  const [agenzie, setAgenzie] = useState([]);
  const [reclami, setReclami] = useState([]);
  const [savingIsp, setSavingIsp] = useState(false);
  const [savingAge, setSavingAge] = useState(false);
  const [savingRec, setSavingRec] = useState(false);
  const [openTab, setOpenTab] = useState(null); // di default tutti i tab sono chiusi (collassati)
  const toggleTab = (k) => setOpenTab((cur) => (cur === k ? null : k));

  useEffect(() => {
    const apply = (src) => {
      setIspettorati(Array.isArray(src?.ispettorati) ? src.ispettorati.map((x) => ({ ...x })) : []);
      setAgenzie(Array.isArray(src?.agenzie) ? src.agenzie.map((x) => ({ ...x })) : []);
      setReclami(Array.isArray(src?.uffici_reclami) ? src.uffici_reclami.map((x) => ({ ...x })) : []);
    };
    apply(rec);
    if (!rec?.id) return;
    // ricarica dal server: la stessa voce potrebbe essere stata modificata dalla pratica
    api.get("/anagrafiche/compagnie").then(({ data }) => {
      const fresh = (data || []).find((c) => c.id === rec.id);
      if (fresh) apply(fresh);
    }).catch(() => {});
  }, [rec]);

  const compName = rec?.denominazione || rec?.ragione_sociale || "";
  const [pool, setPool] = useState({});
  useEffect(() => {
    api.get("/anagrafiche/compagnie").then(({ data }) => setPool(ispPool(data))).catch(() => setPool({}));
  }, []);

  const cleanRows = (rows, cols) => rows
    .map((r) => buildRow(r, cols))
    .filter((r) => cols.some((c) => r[c.key]));

  // Sincronizza le sottosezioni verso il form principale (permette a "Salva modifiche" di persistere tutto insieme)
  useEffect(() => {
    if (!onChange) return;
    onChange({
      ispettorati: cleanRows(ispettorati, ISP_COLS),
      agenzie: cleanRows(agenzie, AGE_COLS),
      uffici_reclami: cleanRows(reclami, RECLAMI_COLS),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ispettorati, agenzie, reclami]);

  const saveIsp = async () => {
    setSavingIsp(true);
    try {
      const data = cleanRows(ispettorati, ISP_COLS);
      const { data: doc } = await api.patch(`/anagrafiche/compagnie/${rec.id}`, { ispettorati: data });
      setIspettorati(data);
      toast.success("Ispettorati salvati");
      window.dispatchEvent(new Event("pgp:compagnie-updated"));
      onSaved && onSaved(doc);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingIsp(false); }
  };

  const saveAge = async () => {
    setSavingAge(true);
    try {
      const data = cleanRows(agenzie, AGE_COLS);
      const { data: doc } = await api.patch(`/anagrafiche/compagnie/${rec.id}`, { agenzie: data });
      setAgenzie(data);
      toast.success("Agenzie salvate");
      window.dispatchEvent(new Event("pgp:compagnie-updated"));
      onSaved && onSaved(doc);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingAge(false); }
  };

  const saveRec = async () => {
    setSavingRec(true);
    try {
      const data = cleanRows(reclami, RECLAMI_COLS);
      const { data: doc } = await api.patch(`/anagrafiche/compagnie/${rec.id}`, { uffici_reclami: data });
      setReclami(data);
      toast.success("Ufficio Reclami salvato");
      window.dispatchEvent(new Event("pgp:compagnie-updated"));
      onSaved && onSaved(doc);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingRec(false); }
  };

  if (!rec) return null;

  const TABS_DEF = [
    { k: "ispettorati", label: "Ispettorati", Icon: ShieldCheck },
    { k: "agenzie", label: "Agenzie", Icon: Building2 },
    { k: "reclami", label: "Ufficio Reclami", Icon: Inbox },
    { k: "liquidatori", label: "Liquidatori", Icon: BadgeEuro },
  ];

  return (
    <div className="mt-1 min-w-0 space-y-2">
      <div className="flex flex-wrap gap-1">
        {TABS_DEF.map(({ k, label, Icon }) => {
          const isOpen = openTab === k;
          return (
            <button key={k} type="button" onClick={() => toggleTab(k)} data-testid={`compagnia-tab-${k}`}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium border transition-colors ${
                isOpen ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "bg-slate-900/40 border-slate-700 text-slate-300 hover:bg-slate-800"
              }`}>
              <Icon className="h-4 w-4" /> {label}
              {isOpen ? <ChevronDown className="h-3.5 w-3.5 opacity-70" /> : <ChevronRight className="h-3.5 w-3.5 opacity-70" />}
            </button>
          );
        })}
      </div>

      {openTab === "ispettorati" && (
        <div className="min-w-0" data-testid="compagnia-panel-ispettorati">
          <SectionTable icon={ShieldCheck} title="Ispettorato Sinistri" color="#1e3a8a" columns={ISP_COLS}
            rows={ispettorati} setRows={setIspettorati} onSave={saveIsp} saving={savingIsp} testId="isp" pool={pool} />
          <LiquidatoriPerIspettorato ispettorati={ispettorati} compName={compName} />
        </div>
      )}
      {openTab === "agenzie" && (
        <div className="min-w-0" data-testid="compagnia-panel-agenzie">
          <SectionTable icon={Building2} title="Agenzie" color="#166534" columns={AGE_COLS}
            rows={agenzie} setRows={setAgenzie} onSave={saveAge} saving={savingAge} testId="age" pool={pool} />
          <EntriesSummary testId="age" rows={agenzie} cols={AGE_COLS} />
        </div>
      )}
      {openTab === "reclami" && (
        <div className="min-w-0" data-testid="compagnia-panel-reclami">
          <SectionTable icon={Inbox} title="Ufficio Reclami" color="#9a3412" columns={RECLAMI_COLS}
            rows={reclami} setRows={setReclami} onSave={saveRec} saving={savingRec} testId="rec" pool={pool}
            newRow={() => ({ denominazione: compName })} />
          <EntriesSummary testId="rec" rows={reclami} cols={RECLAMI_COLS} />
        </div>
      )}
      {openTab === "liquidatori" && (
        <div className="min-w-0" data-testid="compagnia-panel-liquidatori">
          <LiquidatoriTab compName={compName} />
        </div>
      )}
    </div>
  );
}
